// The playbook is stored as PLAYBOOK.md — YAML frontmatter (name, description)
// plus a markdown body that the agent's runtime surfaces to the model. The
// guided editor never shows that format: it edits these fields and (de)serializes
// here, so a non-technical owner only ever sees "name / description / instructions".

export type SuggestedServer = { name: string; why: string };

export type PlaybookTemplate = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  instructions: string;
  suggestedServers: SuggestedServer[];
};

export type PlaybookFields = { name: string; description: string; body: string };

const FRONTMATTER = /^---\n([\s\S]*?)\n---\n?/;

// parsePlaybook splits a PLAYBOOK.md into the editable fields. A file with no
// frontmatter still loads (everything becomes the body) so hand-written files
// round-trip into the guided editor.
export function parsePlaybook(content: string): PlaybookFields {
  const m = content.match(FRONTMATTER);
  if (!m) return { name: "", description: "", body: content.trim() };
  const fm = m[1];
  const body = content.slice(m[0].length).replace(/^\n+/, "");
  return { name: fmValue(fm, "name"), description: fmValue(fm, "description"), body: body.trim() };
}

// serializePlaybook produces valid YAML frontmatter + body. Values are always
// double-quoted (and escaped) so any punctuation in name/description stays valid.
export function serializePlaybook(f: PlaybookFields): string {
  const name = f.name.trim();
  const description = f.description.trim();
  const body = f.body.trim();
  return `---\nname: ${yaml(name)}\ndescription: ${yaml(description)}\n---\n\n${body}\n`;
}

function fmValue(fm: string, key: string): string {
  for (const line of fm.split("\n")) {
    const m = line.match(new RegExp(`^${key}:\\s*(.*)$`));
    if (m) return unquote(m[1].trim());
  }
  return "";
}

function unquote(s: string): string {
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    return s.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  }
  return s;
}

function yaml(s: string): string {
  return `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, " ")}"`;
}

// parseRequiresTools reads the requires_tools list from a PLAYBOOK.md's YAML
// frontmatter — the integrations the agent relies on. Supports a flow list
// (requires_tools: [a, b]) and a simple block list (requires_tools:\n  - a).
// Returns [] when absent.
export function parseRequiresTools(content: string): string[] {
  const m = content.match(FRONTMATTER);
  if (!m) return [];
  const fm = m[1];
  const strip = (s: string) => s.trim().replace(/^["']|["']$/g, "").trim();

  const flow = fm.match(/^requires_tools:\s*\[(.*)\]\s*$/m);
  if (flow) return flow[1].split(",").map(strip).filter(Boolean);

  const lines = fm.split("\n");
  const idx = lines.findIndex((l) => /^requires_tools:\s*$/.test(l));
  if (idx === -1) return [];
  const out: string[] = [];
  for (let i = idx + 1; i < lines.length; i++) {
    const item = lines[i].match(/^\s*-\s*(.+?)\s*$/);
    if (!item) break;
    out.push(strip(item[1]));
  }
  return out.filter(Boolean);
}

// decodeBase64Text turns a base64 file payload into UTF-8 text.
export function decodeBase64Text(b64: string): string {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
}

// isExternalHref distinguishes a link that should open in the browser (a scheme
// like https:/mailto:, a page anchor, or a server-absolute path) from a relative
// path that points at a sibling workspace file.
export function isExternalHref(href: string): boolean {
  return /^([a-z][a-z0-9+.-]*:|#|\/)/i.test(href);
}

// resolveRelativePath resolves a relative link (href) against the directory of
// the file it appears in, collapsing ./ and ../ and dropping any #fragment or
// ?query, into a clean workspace-relative path (e.g. fromDir "examples",
// href "../PLAYBOOK.md" → "PLAYBOOK.md").
export function resolveRelativePath(fromDir: string, href: string): string {
  const clean = href.split("#")[0].split("?")[0];
  const out = fromDir ? fromDir.split("/") : [];
  for (const seg of clean.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") {
      out.pop();
      continue;
    }
    out.push(seg);
  }
  return out.join("/");
}
